import {Action, getModule, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import store from '@/store';
import {PModuleType} from '@/enums/configs';
import {MeasurementType} from '@/views/system/measurements/enums';
import {PMBoundaryMessageLevel, PMProcessStatus, PMWindowType} from '@/views/system/modules/enums';
import {httpClient} from '@/api/baseApi';
import {EnumData} from '@/interfaces/config';
import {MachineWorkingStatus} from '@/components/system/enums';
import {NodeDataType} from '@/system/nodes/enums';
import {SpectrumEventType} from '@/enums/diagrams';

/*
    Динамический модуль хранилища vuex для работы с перечислениями
*/

const moduleName = 'enumsModule';

/*
    module_type
*/

export const moduleTypeToIdMap: {[k: string]: PModuleType} = {
  DtcpTahoModule: PModuleType.DtcpTahoModule,
  CrmModule: PModuleType.CrmModule,
  DtcpServerModule: PModuleType.DtcpServerModule,
  DtcpEnergyModule: PModuleType.DtcpEnergyModule,
  DtcpTemperatureModule: PModuleType.DtcpTemperatureModule,
  DtcpAccelerometerModule: PModuleType.DtcpAccelerometerModule,
  HeaterMonitorModule: PModuleType.HeaterMonitorModule,
  BearingControlModule: PModuleType.BearingControlModule,
  CleanStorageModule: PModuleType.CleanStorageModule,
  ServerMonitorModule: PModuleType.ServerMonitorModule,
  PingerModule: PModuleType.PingerModule,
  DtcpEmulatorModule: PModuleType.DtcpEmulatorModule,
  StatsCollectorModule: PModuleType.StatsCollectorModule,
  SignalTrendModule: PModuleType.SignalTrendModule,
  SignalTrendServerModule: PModuleType.SignalTrendServerModule,
  MonitorStatesTasks: PModuleType.MonitorStatesTasks,
  MonitorBoundaryTasks: PModuleType.MonitorBoundaryTasks,
  MonitorChildTasks: PModuleType.MonitorChildTasks,
};

// TODO: с сервера возвращается строка
export function transformFromModuleTypeStr(moduleType: string): PModuleType {
  const id = moduleTypeToIdMap[moduleType];
  if (id !== null && id !== undefined) {
    return id;
  }
  throw Error(`Not client implemented for ${moduleType}!`);
}

function transformEnum<T>(elem: EnumData<any>): EnumData<T> {
  return {
    value: elem.value as T,
    description: elem.description,
    url: elem.url,
    regnum_type: elem.regnum_type,
  };
}

@Module({
  name: moduleName,
  store: store,
  dynamic: true,
  namespaced: true,
})
export class EnumsModule extends VuexModule {
  public windowTypes: EnumData<PMWindowType>[] = [];

  public boundaryMessageLevels: EnumData<PMBoundaryMessageLevel>[] = [];

  public processStatuses: EnumData<PMProcessStatus>[] = [];

  public measurementTypes: EnumData<MeasurementType>[] = [];

  public moduleTypes: EnumData<PModuleType>[] = [];

  public spectrumTypes: EnumData<SpectrumEventType>[] = [];

  public machineWorkingStatuses: EnumData<MachineWorkingStatus>[] = [];

  public nodeDataTypes: EnumData<NodeDataType>[] = [];

  public loaded = false;

  @Mutation
  setAll(data: {
    windowTypes: EnumData<PMWindowType>[];
    boundaryMessageLevels: EnumData<PMBoundaryMessageLevel>[];
    processStatuses: EnumData<PMProcessStatus>[];
    measurementTypes: EnumData<MeasurementType>[];
    moduleTypes: EnumData<PModuleType>[];
    machineWorkingStatuses: EnumData<MachineWorkingStatus>[];
    nodeDataTypes: EnumData<NodeDataType>[];
    spectrumTypes: EnumData<SpectrumEventType>[];
  }) {
    this.windowTypes = data.windowTypes;
    this.boundaryMessageLevels = data.boundaryMessageLevels;
    this.processStatuses = data.processStatuses;
    this.measurementTypes = data.measurementTypes;
    this.moduleTypes = data.moduleTypes;
    this.machineWorkingStatuses = data.machineWorkingStatuses;
    this.nodeDataTypes = data.nodeDataTypes;
    this.spectrumTypes = data.spectrumTypes;
    this.loaded = true;
  }

  @Action
  public async fetchAll() {
    try {
      const windowTypesResponse: {data: any[]} = await httpClient.get('window_types/');
      const windowTypes: EnumData<PMWindowType>[] = windowTypesResponse.data.map((elem: any) =>
        transformEnum<PMWindowType>(elem),
      );

      const boundaryMessageLevelsResponse: {data: any[]} = await httpClient.get(
        'boundary_message_levels/',
      );
      const boundaryMessageLevels: EnumData<
        PMBoundaryMessageLevel
      >[] = boundaryMessageLevelsResponse.data.map((elem: any) =>
        transformEnum<PMBoundaryMessageLevel>(elem),
      );

      const processStatusesResponse: {data: any[]} = await httpClient.get(
        'module_process_statuses/',
      );
      const processStatuses: EnumData<
        PMProcessStatus
      >[] = processStatusesResponse.data.map((elem: any) => transformEnum<PMProcessStatus>(elem));

      const measurementTypesResponse: {data: any[]} = await httpClient.get('measurement_types/');
      const measurementTypes: EnumData<
        MeasurementType
      >[] = measurementTypesResponse.data.map((elem: any) => transformEnum<MeasurementType>(elem));

      const moduleTypesResponse: {data: any[]} = await httpClient.get('module_types/');
      const moduleTypes: EnumData<PModuleType>[] = moduleTypesResponse.data.map((elem: any) =>
        transformEnum<PModuleType>(elem),
      );

      //TODO когда на сервере появится апи для справочника переделать
      const machineWorkingStatuses: EnumData<MachineWorkingStatus>[] = [
        {value: MachineWorkingStatus.Off, description: 'Выключено'},
        {value: MachineWorkingStatus.Idle, description: 'Холостой режим'},
        {value: MachineWorkingStatus.Work, description: 'Рабочий режим'},
        {value: MachineWorkingStatus.Incorrect, description: 'Некорректная работа'},
      ].map((elem: any) => transformEnum<MachineWorkingStatus>(elem));

      const nodeDataTypesResponse: {data: any[]} = await httpClient.get('node_types/');
      const nodeDataTypes: EnumData<NodeDataType>[] = nodeDataTypesResponse.data.map((elem: any) =>
        transformEnum<NodeDataType>(elem),
      );

      const spectrumTypesResponse: {data: any[]} = await httpClient.get('spectrum_types/');
      const spectrumTypes: EnumData<
        SpectrumEventType
      >[] = spectrumTypesResponse.data.map((elem: any) => transformEnum<SpectrumEventType>(elem));

      this.context.commit('setAll', {
        windowTypes,
        boundaryMessageLevels,
        processStatuses,
        measurementTypes,
        moduleTypes,
        machineWorkingStatuses,
        nodeDataTypes,
        spectrumTypes,
      });
    } catch (e) {
      return Promise.reject(e);
    }
  }
}

export default getModule(EnumsModule);
